import { useState, useEffect, useCallback, useRef } from 'react';
import { Heart, Users, MessageCircle, Settings, Check, X, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import { Contact, Interaction } from '../types';
import { syncApi, contactsApi, interactionsApi } from '../lib/api';

type StepStatus = 'pending' | 'active' | 'done' | 'failed';

interface SyncStep {
  key: string;
  label: string;
  activeLabel: string;
  icon: typeof Users;
  status: StepStatus;
  detail?: string;
}

interface SyncScreenProps {
  guestContacts: Contact[];
  guestInteractions: Interaction[];
  guestWeeklyGoal?: number;
  onComplete: (freshData: { contacts: unknown[]; interactions: unknown[] }) => void;
  onSkip: () => void;
}

export default function SyncScreen({
  guestContacts,
  guestInteractions,
  guestWeeklyGoal,
  onComplete,
  onSkip,
}: SyncScreenProps) {
  const [steps, setSteps] = useState<SyncStep[]>([
    {
      key: 'contacts',
      label: 'Contacts synced',
      activeLabel: 'Syncing contacts...',
      icon: Users,
      status: 'pending',
    },
    {
      key: 'interactions',
      label: 'Interactions synced',
      activeLabel: 'Syncing interactions...',
      icon: MessageCircle,
      status: 'pending',
    },
    {
      key: 'finalize',
      label: 'Settings & health scores',
      activeLabel: 'Calculating health scores...',
      icon: Settings,
      status: 'pending',
    },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [fadeOut, setFadeOut] = useState(false);
  const contactMapRef = useRef<Record<string, number>>({});
  const runningRef = useRef(false);

  const updateStep = useCallback((key: string, status: StepStatus, detail?: string) => {
    setSteps((prev) =>
      prev.map((s) => (s.key === key ? { ...s, status, detail: detail ?? s.detail } : s)),
    );
  }, []);

  const runSync = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setError(null);

    // Find which step to resume from
    const currentSteps = steps;
    const failedStep = currentSteps.find((s) => s.status === 'failed');
    const startFrom = failedStep?.key || 'contacts';
    const stepOrder = ['contacts', 'interactions', 'finalize'];
    const startIdx = stepOrder.indexOf(startFrom);

    try {
      // Step 1: Contacts
      if (startIdx <= 0) {
        updateStep('contacts', 'active', `0 of ${guestContacts.length}`);
        const contactResult = await syncApi.migrateContacts({
          contacts: guestContacts.map((c) => ({
            local_id: c.id,
            name: c.name,
            relationship_type: c.relationshipType || 'Friend',
            contact_frequency_days: c.contactFrequencyDays,
            birthday: c.birthday,
            tags: c.tags,
            year_met: c.yearMet ? parseInt(c.yearMet, 10) : undefined,
            inner_circle: c.innerCircle,
            communication_channels: c.communicationMethods,
            photo_url: c.photoUrl,
            preferred_comm_method: c.preferredCommMethod,
          })),
        });
        contactMapRef.current = contactResult.contactMap;
        updateStep(
          'contacts',
          'done',
          `${contactResult.syncedContacts.length} contact${contactResult.syncedContacts.length !== 1 ? 's' : ''}`,
        );
      }

      // Step 2: Interactions
      if (startIdx <= 1) {
        updateStep('interactions', 'active', `0 of ${guestInteractions.length}`);
        const interactionResult = await syncApi.migrateInteractions({
          contact_map: contactMapRef.current,
          interactions: guestInteractions.map((i) => ({
            local_contact_id: i.contactId,
            interaction_type: i.type,
            interaction_date: i.date,
            notes: i.notes,
            rating: i.rating,
            attachments: i.attachments,
          })),
        });
        updateStep(
          'interactions',
          'done',
          `${interactionResult.syncedInteractions} interaction${interactionResult.syncedInteractions !== 1 ? 's' : ''}`,
        );
      }

      // Step 3: Finalize
      updateStep('finalize', 'active');
      const notes = guestContacts.flatMap((c) =>
        (c.notes || []).map((n) => ({
          local_contact_id: c.id,
          text: n.text,
          created_at: n.date,
        })),
      );
      await syncApi.migrateFinalize({
        contact_map: contactMapRef.current,
        notes,
        weekly_goal_target: guestWeeklyGoal,
      });
      updateStep('finalize', 'done');

      // Fetch fresh data from API
      const [contactsRes, interactionsRes] = await Promise.all([
        contactsApi.list({ limit: 100 }),
        interactionsApi.list({ limit: 100 }),
      ]);

      // Brief delay for the user to see "all done" state
      setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => {
          onComplete({
            contacts: contactsRes.data,
            interactions: interactionsRes.data,
          });
        }, 400);
      }, 800);
    } catch (err) {
      // Mark the current active step as failed
      setSteps((prev) => prev.map((s) => (s.status === 'active' ? { ...s, status: 'failed' } : s)));
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Your local data is safe.',
      );
    } finally {
      runningRef.current = false;
    }
  }, [steps, guestContacts, guestInteractions, guestWeeklyGoal, onComplete, updateStep]);

  // Auto-start on mount
  useEffect(() => {
    runSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    setError(null);
    runSync();
  };

  const allDone = steps.every((s) => s.status === 'done');

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#f8f5f5] transition-opacity duration-400 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Header */}
      <div className="flex flex-col items-center gap-4 mb-10">
        <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center">
          <Heart size={32} className="text-[#fa8585] fill-[#fa8585]" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800">
            {allDone ? 'All synced!' : 'Syncing your data'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {allDone
              ? 'Your contacts and interactions are ready'
              : "Transferring your data to your account. Don't close the app."}
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="w-full max-w-sm px-6 space-y-3">
        {steps.map((step) => (
          <div
            key={step.key}
            className={`flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 ${
              step.status === 'active'
                ? 'bg-white shadow-sm'
                : step.status === 'done'
                  ? 'bg-white/60'
                  : step.status === 'failed'
                    ? 'bg-red-50'
                    : 'bg-white/40'
            }`}
          >
            {/* Icon */}
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                step.status === 'done'
                  ? 'bg-emerald-100'
                  : step.status === 'failed'
                    ? 'bg-red-100'
                    : step.status === 'active'
                      ? 'bg-[#fa8585]/10'
                      : 'bg-slate-100'
              }`}
            >
              <AnimatePresence mode="wait">
                {step.status === 'done' ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                  >
                    <Check size={20} className="text-emerald-600" />
                  </motion.div>
                ) : step.status === 'failed' ? (
                  <X size={20} className="text-red-500" />
                ) : step.status === 'active' ? (
                  <motion.div
                    key="spinner"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <step.icon size={20} className="text-[#fa8585]" />
                  </motion.div>
                ) : (
                  <step.icon size={20} className="text-slate-400" />
                )}
              </AnimatePresence>
            </div>

            {/* Label */}
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium ${
                  step.status === 'active'
                    ? 'text-slate-800'
                    : step.status === 'done'
                      ? 'text-emerald-700'
                      : step.status === 'failed'
                        ? 'text-red-700'
                        : 'text-slate-400'
                }`}
              >
                {step.status === 'active'
                  ? step.activeLabel
                  : step.status === 'done'
                    ? step.label
                    : step.status === 'failed'
                      ? 'Failed'
                      : step.label}
              </p>
              {step.detail && step.status === 'done' && (
                <p className="text-xs text-slate-400 mt-0.5">{step.detail}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Error + Actions */}
      {error && (
        <div className="w-full max-w-sm px-6 mt-6 space-y-3">
          <p className="text-sm text-red-600 text-center">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={handleRetry}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#fa8585] text-white rounded-xl font-medium text-sm active:scale-95 transition-transform"
            >
              <RotateCcw size={16} />
              Retry
            </button>
            <button
              onClick={onSkip}
              className="flex-1 py-3 bg-white text-slate-600 rounded-xl font-medium text-sm border border-slate-200 active:scale-95 transition-transform"
            >
              Skip for now
            </button>
          </div>
          <p className="text-xs text-slate-400 text-center">
            Your local data is preserved. You can sync again later.
          </p>
        </div>
      )}
    </div>
  );
}
